import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { toLower } from 'lodash-es'
import { toast } from 'react-toastify'

import { useGetJobStatusQuery } from 'app/services/api/jobs'
import { downloadFileFromJob } from 'common/utils/exportUtils'

import {
  CreateDataExportJob,
  IExportJobContext,
  IJobExportStatus
} from './types'

const noJob = { jobId: '' }

interface IJobResult {
  jobId: string
  queuePosition: number
}

export const ExportJobContext = createContext<IExportJobContext>({
  exportJob: {},
  jobStatus: '',
  isJobPending: false,
  isExporting: false,
  createDataExportJob: Promise.resolve
})

export const ExportJobProvider = ({ children }: React.PropsWithChildren) => {
  const [{ jobId, fileName, extension }, setJob] =
    useState<IJobExportStatus>(noJob)

  const [isExporting, setIsExporting] = useState(false)
  const exportingToastId = useRef<number | string>()

  const exportJob = useGetJobStatusQuery(jobId || skipToken)

  const { currentData, refetch, isUninitialized } = exportJob
  const jobStatus = toLower(currentData?.jobStatus)
  const isJobPending = ['processing', 'pending'].includes(
    jobStatus.toLocaleLowerCase()
  )

  const intervalID = useRef<NodeJS.Timeout>()

  const clearExportingToast = useCallback(() => {
    if (exportingToastId.current != null) {
      toast.dismiss(exportingToastId.current)
      exportingToastId.current = undefined
    }
  }, [])

  useEffect(() => {
    if (jobId && fileName && jobStatus) {
      const _onSuccess = (succesText: string) => {
        clearExportingToast()
        toast.success(succesText)
      }

      const _onFail = (failText: string) => {
        clearExportingToast()
        toast.error(failText)
      }

      const _stopPolling = () => {
        clearInterval(intervalID.current)
        intervalID.current = undefined
        setIsExporting(false)
        setJob(noJob)
      }

      // not an ideal way to check for the status change from the server
      // server should create a stream or dispatch a socket
      if (
        !isUninitialized &&
        isJobPending &&
        intervalID.current === undefined
      ) {
        intervalID.current = setInterval(() => {
          refetch().catch(() => {
            _stopPolling()
            _onFail('Export unsuccessful')
          })
        }, 5555)
      } else if (jobStatus === 'success') {
        _stopPolling()

        downloadFileFromJob(jobId, fileName, extension)
          .then(_onSuccess)
          .catch(_onFail)
      } else if (jobStatus === 'rejected' || jobStatus === 'error') {
        _stopPolling()
        _onFail('Export unsuccessful')
      }
    }
  }, [
    clearExportingToast,
    isUninitialized,
    jobStatus,
    jobId,
    fileName,
    extension,
    isJobPending,
    refetch
  ])

  const createDataExportJob = useCallback<CreateDataExportJob>(
    (mutationTrigger, mutationArgs, fileName, extension = '.zip') => {
      setIsExporting(true)
      exportingToastId.current = toast.info('Exporting')

      return mutationTrigger(mutationArgs)
        .unwrap()
        .then((jobResult: IJobResult) => {
          setJob({
            ...jobResult,
            fileName: fileName || `Export-${jobResult.jobId}`,
            extension
          })
        })
        .catch(() => {
          clearExportingToast()
          setIsExporting(false)
          setJob(noJob)
          toast.error('Export unsuccessful')
        })
    },
    [clearExportingToast]
  )

  const contextValue = useMemo(
    () => ({
      exportJob,
      jobStatus,
      isJobPending,
      isExporting,
      createDataExportJob
    }),
    [exportJob, jobStatus, isJobPending, isExporting, createDataExportJob]
  )

  return (
    <ExportJobContext.Provider value={contextValue}>
      {children}
    </ExportJobContext.Provider>
  )
}
